import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { Building2, Users, LogOut, FileText, FlaskConical, Wand2, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";
import { useEffect, useState } from "react";

const navItems = [
  { to: "/accounts", label: "Accounts", icon: Building2 },
  { to: "/users", label: "Users", icon: Users },
  { to: "/prompts", label: "Prompts", icon: FileText },
  { to: "/playground", label: "Playground", icon: FlaskConical },
  { to: "/wizard-builder", label: "Wizard Builder", icon: Wand2 },
];

const COLLAPSED_KEY = "zitiu.backoffice.sidebarCollapsed";

export function AppLayout() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem(COLLAPSED_KEY) === "1"; } catch { return false; }
  });

  useEffect(() => {
    try { localStorage.setItem(COLLAPSED_KEY, collapsed ? "1" : "0"); } catch {}
  }, [collapsed]);

  const handleSignOut = async () => {
    await signOut();
    navigate("/login", { replace: true });
  };

  return (
    <div className="flex min-h-screen bg-background">
      {/* Sidebar */}
      <aside
        className={cn(
          "border-r border-border bg-card flex flex-col transition-[width] duration-200 ease-out shrink-0",
          collapsed ? "w-14" : "w-60"
        )}
      >
        <div className={cn("flex items-center border-b border-border", collapsed ? "p-2 justify-center" : "p-4 justify-between gap-2")}>
          {!collapsed && (
            <h1 className="font-display text-lg font-bold text-foreground truncate">Zitiu</h1>
          )}
          <button
            type="button"
            onClick={() => setCollapsed((c) => !c)}
            className="p-1.5 rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? <PanelLeftOpen className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
          </button>
        </div>

        <nav className={cn("flex-1 space-y-1", collapsed ? "p-1.5" : "p-2")}>
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              title={collapsed ? item.label : undefined}
              className={({ isActive }) =>
                cn(
                  "flex items-center rounded-md text-sm font-medium transition-colors",
                  collapsed ? "justify-center p-2.5" : "gap-2 px-3 py-2",
                  isActive
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                )
              }
            >
              <item.icon className="w-4 h-4 shrink-0" />
              {!collapsed && <span className="truncate">{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        <div className={cn("border-t border-border", collapsed ? "p-2" : "p-4")}>
          {!collapsed && (
            <p className="text-xs text-muted-foreground truncate mb-2">{user?.email}</p>
          )}
          <button
            onClick={handleSignOut}
            title={collapsed ? "Sign out" : undefined}
            className={cn(
              "flex items-center text-sm text-muted-foreground hover:text-foreground transition-colors",
              collapsed ? "justify-center w-full" : "gap-2"
            )}
          >
            <LogOut className="w-4 h-4 shrink-0" />
            {!collapsed && "Sign out"}
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 min-w-0 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
}
